(() => {
  // Shared base: keeps the main product form's variant id in sync with the
  // sticky / secondary variant picker so add-to-cart submits the right variant.
  class SecondaryVariantSelectBase extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.variants = this.parseVariants();
      if (!this.variants) return;

      this.querySelectorAll('select').forEach(select => {
        select.addEventListener('change', () => this.onChange());
      });
    }

    parseVariants() {
      const json = this.querySelector('script[type="application/json"]');
      if (!json) return null;
      try {
        return JSON.parse(json.textContent);
      } catch (e) {
        console.error('secondary-variant-select: invalid variants JSON', e);
        return null;
      }
    }

    // Subclasses return an array like ['Red', 'M'] of selected option values.
    getSelectedOptions() {
      return [];
    }

    findVariant(selectedOptions) {
      return this.variants.find(v =>
        selectedOptions.every((val, i) => v.options[i] === val)
      );
    }

    onChange() {
      const selected = this.getSelectedOptions();
      if (!selected || selected.some(v => v == null)) return;

      const variant = this.findVariant(selected);
      if (!variant) return;

      this.currentVariantId = variant.id;
      this.updateMainForm(variant);
      this.syncMainPicker(selected);
    }

    // Public: returns the variant id currently selected in this picker.
    getSelectedVariantId() {
      if (this.currentVariantId) return this.currentVariantId;
      // Compute on first call (before any change event has fired).
      const selected = this.getSelectedOptions();
      if (!selected || selected.some(v => v == null)) return null;
      const variant = this.findVariant(selected);
      return variant ? variant.id : null;
    }

    updateMainForm(variant) {
      // Update every name="id" input associated with this section's form.
      const formId = `product-form-${this.sectionId}`;
      const mainForm = document.getElementById(formId);
      if (mainForm) {
        mainForm.querySelectorAll('input[name="id"]').forEach(input => {
          input.value = variant.id;
          input.removeAttribute('disabled');
        });
      }

      // Also update via the <product-form> element's own cached reference so
      // FormData picks up the new value even if the element cached the input.
      const productFormEl = document.querySelector(
        `product-form#ProductForm-${this.sectionId}, product-form[data-section-id="${this.sectionId}"]`
      );
      if (productFormEl) {
        const inner = productFormEl.querySelector('input[name="id"]');
        if (inner) {
          inner.value = variant.id;
          inner.removeAttribute('disabled');
        }
        // Update the element's own variantIdInput getter cache if it exists.
        if (productFormEl.variantIdInput) {
          productFormEl.variantIdInput.value = variant.id;
          productFormEl.variantIdInput.disabled = false;
        }
      }
    }

    // Try to mirror selection into the main <product-variant-selects> so that
    // its existing change-listener updates price, media, URL, etc.
    syncMainPicker(selectedOptions) {
      const mainPicker = document.querySelector(
        `product-variant-selects#product-variant-selects-${this.sectionId}`
      );
      if (!mainPicker) return;

      const selects = mainPicker.querySelectorAll('select');
      selects.forEach((select, index) => {
        const value = selectedOptions[index];
        if (value == null) return;
        if (select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Radio / button pickers
      const fieldsets = mainPicker.querySelectorAll('fieldset');
      fieldsets.forEach((fieldset, index) => {
        const value = selectedOptions[index];
        if (value == null) return;
        const input = fieldset.querySelector(
          `input[type="radio"][value="${CSS.escape(value)}"]`
        );
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  // Combined: single <select class="sticky-atc__variant-select"> whose option
  // value is "opt1,opt2,opt3".
  if (!customElements.get('secondary-variant-select')) {
    customElements.define(
      'secondary-variant-select',
      class extends SecondaryVariantSelectBase {
        getSelectedOptions() {
          const select = this.querySelector('.sticky-atc__variant-select');
          if (!select) return null;
          const opt = select.options[select.selectedIndex];
          const raw = (opt && opt.dataset.options) || select.value || '';
          return raw.split(',').map(s => s.trim());
        }
      }
    );
  }

  // Separate: one <select name="options[OptionName]"> per option.
  if (!customElements.get('secondary-variant-select-separate')) {
    customElements.define(
      'secondary-variant-select-separate',
      class extends SecondaryVariantSelectBase {
        getSelectedOptions() {
          return Array.from(
            this.querySelectorAll('select[name^="options["]')
          ).map(s => s.value);
        }
      }
    );
  }
})();
